import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Printer, CheckCircle2, XCircle, Clock, MapPin, Users, Calendar, Info, ShieldAlert, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VoucherPage() {
  const { voucherNumber } = useParams();
  const [voucher, setVoucher] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/vouchers/${voucherNumber}`)
      .then((res) => setVoucher(res.data))
      .catch(() => setError(true));
  }, [voucherNumber]);

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4" data-testid="voucher-not-found">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Voucher Not Found</h1>
        <p className="text-gray-600 mb-6">The voucher number "{voucherNumber}" does not exist.</p>
        <Link to="/" className="text-[#002868] underline font-semibold" data-testid="voucher-back-home-link">Back to Home</Link>
      </div>
    );
  }

  if (!voucher) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="voucher-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C9A227]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 print:bg-white print:py-0" data-testid="voucher-page">
      <Helmet>
        <title>Tour Voucher {voucher.voucher_number} | Official Hollywood Tours</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-3xl mx-auto mb-4 flex items-center justify-between print:hidden">
        <Link to="/" className="inline-flex items-center gap-2 text-[#002868] font-semibold hover:underline" data-testid="voucher-home-link">
          <ArrowLeft className="w-4 h-4" /> OfficialHollywoodTours.com
        </Link>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 bg-[#002868] text-white px-5 py-2.5 rounded-full font-semibold hover:bg-[#001d4d] transition-colors"
          data-testid="voucher-print-button"
        >
          <Printer className="w-4 h-4" /> Print / Save PDF
        </button>
      </div>

      <div className="max-w-3xl mx-auto bg-white shadow-xl print:shadow-none rounded-lg overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#C9A227] to-[#E5C158] px-8 py-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <img src="/images/official-hollywood-tours-logo.png" alt="Official Hollywood Tours" className="h-14 mb-2" onError={(e) => { e.target.style.display = 'none'; }} />
              <h1 className="text-2xl font-black text-black tracking-tight">OFFICIAL TOUR VOUCHER</h1>
              <p className="text-sm text-gray-800 font-medium">OfficialHollywoodTours.com · Vegas2Hollywood.com · IATA Accredited</p>
            </div>
            <div className="text-right">
              <span className="inline-flex items-center gap-1.5 bg-green-700 text-white text-sm font-bold px-4 py-1.5 rounded-full" data-testid="voucher-status-badge">
                <CheckCircle2 className="w-4 h-4" /> {voucher.status}
              </span>
              <p className="mt-2 text-sm font-mono font-bold text-black" data-testid="voucher-number">{voucher.voucher_number}</p>
            </div>
          </div>
        </div>

        {/* Key details */}
        <div className="px-8 py-6 border-b border-gray-200">
          <h2 className="text-lg font-bold text-[#B22234] mb-1" data-testid="voucher-tour-name">{voucher.tour_name}</h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
            <div className="flex items-start gap-2"><Users className="w-4 h-4 text-[#002868] mt-0.5 shrink-0" /><span><strong>Guest:</strong> <span data-testid="voucher-guest-name">{voucher.guest_name}</span></span></div>
            <div className="flex items-start gap-2"><Calendar className="w-4 h-4 text-[#002868] mt-0.5 shrink-0" /><span><strong>Tour Date:</strong> {voucher.tour_date}</span></div>
            <div className="flex items-start gap-2"><Clock className="w-4 h-4 text-[#002868] mt-0.5 shrink-0" /><span><strong>Pick-up Time:</strong> {voucher.pickup_time}</span></div>
            <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-[#002868] mt-0.5 shrink-0" /><span><strong>Pick-up Location:</strong> {voucher.pickup_location}</span></div>
            <div className="flex items-start gap-2 sm:col-span-2"><Users className="w-4 h-4 text-[#002868] mt-0.5 shrink-0" /><span><strong>Group Size:</strong> {voucher.group_size}</span></div>
            {voucher.confirmation_number && (
              <div className="flex items-start gap-2 sm:col-span-2"><CheckCircle2 className="w-4 h-4 text-[#002868] mt-0.5 shrink-0" /><span><strong>Confirmation #:</strong> <span className="font-mono font-bold" data-testid="voucher-confirmation-number">{voucher.confirmation_number}</span></span></div>
            )}
          </div>
        </div>

        {/* Intro */}
        {voucher.intro_message && (
          <div className="px-8 py-5 border-b border-gray-200 text-sm text-gray-700 leading-relaxed" data-testid="voucher-intro">
            {voucher.intro_message}
          </div>
        )}

        {/* Traffic note */}
        {voucher.traffic_note && (
          <div className="mx-8 my-5 p-4 bg-amber-50 border border-amber-300 rounded-lg text-sm text-gray-800" data-testid="voucher-traffic-note">
            <p className="font-bold text-amber-800 mb-1 flex items-center gap-1.5"><Info className="w-4 h-4" /> December 31 Traffic Advisory</p>
            {voucher.traffic_note}
          </div>
        )}

        {/* Pricing */}
        {voucher.pricing && voucher.pricing.length > 0 && (
          <div className="px-8 py-5 border-b border-gray-200" data-testid="voucher-pricing">
            <h3 className="text-base font-black text-[#002868] uppercase tracking-wide mb-3">Total Price</h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {voucher.pricing.map((p, i) => (
                <div key={i} className="flex justify-between items-start gap-4 px-4 py-2.5 text-sm border-b border-gray-100">
                  <span className="text-gray-700">{p.label}</span>
                  <span className="font-bold whitespace-nowrap">{p.amount}</span>
                </div>
              ))}
              {voucher.total && (
                <div className="flex justify-between items-center gap-4 px-4 py-3 bg-gray-50 text-sm">
                  <span className="font-black uppercase tracking-wide">Total</span>
                  <span className="font-black text-[#B22234]" data-testid="voucher-total">{voucher.total}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Itinerary */}
        <div className="px-8 py-5 border-b border-gray-200">
          <h3 className="text-base font-black text-[#002868] uppercase tracking-wide mb-4">Program Itinerary Confirmation</h3>
          <div className="space-y-0" data-testid="voucher-itinerary">
            {voucher.itinerary.map((item, i) => (
              <div key={i} className="flex gap-4 relative pb-4 last:pb-0">
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full bg-[#C9A227] mt-1 shrink-0"></div>
                  {i < voucher.itinerary.length - 1 && <div className="w-px flex-1 bg-gray-300"></div>}
                </div>
                <div className="text-sm pb-1">
                  <span className="font-bold text-[#B22234] font-mono">{item.time}</span>
                  <span className="text-gray-800"> — {item.activity}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Included / Not included */}
        <div className="px-8 py-5 border-b border-gray-200 grid sm:grid-cols-2 gap-6">
          <div data-testid="voucher-included">
            <h3 className="text-base font-black text-[#002868] uppercase tracking-wide mb-3">Included</h3>
            <ul className="space-y-1.5 text-sm text-gray-700">
              {voucher.included.map((item, i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />{item}</li>
              ))}
            </ul>
          </div>
          <div data-testid="voucher-not-included">
            <h3 className="text-base font-black text-[#002868] uppercase tracking-wide mb-3">Not Included</h3>
            <ul className="space-y-1.5 text-sm text-gray-700">
              {voucher.not_included.map((item, i) => (
                <li key={i} className="flex items-start gap-2"><XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />{item}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Notes */}
        {voucher.notes.length > 0 && (
          <div className="px-8 py-5 border-b border-gray-200" data-testid="voucher-notes">
            <h3 className="text-base font-black text-[#002868] uppercase tracking-wide mb-3">Important Notes</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              {voucher.notes.map((note, i) => (
                <li key={i} className="flex items-start gap-2"><Info className="w-4 h-4 text-[#C9A227] mt-0.5 shrink-0" />{note}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Cancellation */}
        {voucher.cancellation_policy && (
          <div className="px-8 py-5 border-b border-gray-200" data-testid="voucher-cancellation">
            <h3 className="text-base font-black text-[#002868] uppercase tracking-wide mb-2 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-[#B22234]" /> Cancellation Policy</h3>
            <p className="text-sm text-gray-700">{voucher.cancellation_policy}</p>
          </div>
        )}

        {/* Closing */}
        {voucher.closing_message && (
          <div className="px-8 py-5 text-sm text-gray-700 italic leading-relaxed" data-testid="voucher-closing">
            {voucher.closing_message}
          </div>
        )}

        {/* Footer */}
        <div className="bg-[#1a1a1a] text-white px-8 py-5 text-center">
          <p className="font-bold">Official Hollywood Tours LLC | IATA Accredited</p>
          <p className="text-xs text-gray-400 mt-1">info@officialhollywoodtours.com · www.officialhollywoodtours.com · www.vegas2hollywood.com</p>
          <p className="text-xs text-gray-500 mt-2">Please present this voucher (printed or digital) to your driver-guide at pick-up.</p>
        </div>
      </div>
    </div>
  );
}
